import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useLayoutEffect } from 'react';
import { getClientRectInfo, useThrottleFn, LucaResizeObserver } from '@/sub-luca-plugin/utils';
import { IClientRectInfo, IScrollViewContext, IScrollViewOptions, IOnScrollData, TScrollIntoViewAlignment } from '@/sub-luca-plugin/types';
import classNames from 'classnames';
import './index.scss';

interface IProps {
    id?: string;
    className?: string;
    children?: any;
    scrollY?: boolean;
    scrollX?: boolean;
    scrollTop?: number;
    scrollLeft?: number;
    lowerThreshold?: number;
    holdingPosition?: boolean;
    showScrollbar?: boolean;
    onScrollToLower?: () => void;
    onScroll?: (data: IOnScrollData) => void;
    onSizeChange?: (data: IOnScrollData) => void;
}

const ScrollView = (
    { className, children, scrollY, scrollX, onScroll, onSizeChange, onScrollToLower, lowerThreshold = 0, showScrollbar = false }: IProps,
    ref: any
) => {
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const resizeObserverRef = useRef<ResizeObserver>();

    const { run: scrollTo } = useThrottleFn(
        (options?: IScrollViewOptions) => {
            const { scrollHeight, scrollWidth } = nodeRef.current!;
            scrollY &&
                nodeRef.current?.scrollTo({
                    top: options?.top === undefined ? scrollHeight : options.top,
                    behavior: options?.animation ? 'smooth' : 'auto',
                });
            scrollX &&
                nodeRef.current?.scrollTo({
                    left: options?.left === undefined ? scrollWidth : options.left,
                    behavior: options?.animation ? 'smooth' : 'auto',
                });
        },
        { wait: 100 }
    );

    const { run: handleScrollToLower } = useThrottleFn(
        (data: IClientRectInfo) => {
            const { clientHeight, scrollTop, scrollHeight } = data;
            if (scrollHeight - clientHeight - scrollTop <= lowerThreshold) {
                onScrollToLower?.();
            }
        },
        { wait: 500 }
    );

    const handleScroll = useCallback(async () => {
        const data = await getClientRectInfo(nodeRef.current);
        // console.log(data);
        if (onScrollToLower) handleScrollToLower(data);
        onScroll?.(data);
    }, []);

    const handleResizeChange = useCallback(async () => {
        const data = await getClientRectInfo(nodeRef.current);
        onSizeChange?.(data);
    }, []);

    useImperativeHandle(
        ref,
        (): IScrollViewContext => ({
            scrollToTop: () => scrollTo({ top: 0 }),
            scrollToBottom: () => scrollTo(),
            scrollTo: options => scrollTo(options),
            scrollIntoView: (selector: string, alignment: TScrollIntoViewAlignment = 'nearest') => {
                nodeRef.current?.querySelector(selector)?.scrollIntoView({ block: alignment, behavior: 'smooth' });
            },
            node: nodeRef.current,
        }),
        []
    );

    useLayoutEffect(() => {
        return () => {
            if (nodeRef.current) {
                resizeObserverRef.current?.unobserve(nodeRef.current);
            }
        };
    }, []);

    useEffect(() => {
        nodeRef.current!.addEventListener('scroll', handleScroll);
        // handleResizeChange();
        resizeObserverRef.current = new LucaResizeObserver(handleResizeChange);
        resizeObserverRef.current.observe(nodeRef.current!);
        return () => {
            nodeRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div
            className={classNames('scroll-view', className, !showScrollbar && 'scroll-view--hide-scroll-bar')}
            style={{ overflowX: scrollX ? 'auto' : 'hidden', overflowY: scrollY ? 'auto' : 'hidden' }}
            ref={nodeRef}
        >
            {children}
        </div>
    );
};

export default forwardRef(ScrollView);
